import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { TrendingUp, Filter, RefreshCw } from 'lucide-react';
import NewsCard from '../components/UI/NewsCard';
import toast from 'react-hot-toast';
import { useI18n } from '../i18n/useI18n';

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  category: string;
  imageUrl: string | null;
  biasScore: number;
  noticiabilityTotal: number;
  scoreJson: { impact: number; reach: number; duration: number };
  sourceName: string;
  publishedAt: string;
}

const HomePage: React.FC = () => {
  const [sortBy, setSortBy] = useState<'date' | 'impact'>('date');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const { t } = useI18n();

  const { data: newsData, isLoading, error, refetch } = useQuery({
    queryKey: ['news', sortBy],
    queryFn: async () => {
      const response = await axios.get('/api/v1/news', { params: { limit: 20 } });
      return response.data;
    }
  });

  const handleRefresh = () => {
    refetch();
    toast.success(t('home.newsRefreshed'));
  };

  const articles: NewsArticle[] = newsData?.data || [];
  
  const filteredAndSortedArticles = articles
    .filter(article => categoryFilter === 'all' || article.category.includes(categoryFilter))
    .sort((a, b) => {
      if (sortBy === 'impact') return b.noticiabilityTotal - a.noticiabilityTotal;
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });

  const featuredArticle = filteredAndSortedArticles[0];
  const regularArticles = filteredAndSortedArticles.slice(1);

  const categories = ['all', 'Geopolitics', 'Economy', 'Climate', 'Health', 'Technology', 'Disasters', 'Migration'];

  if (error) {
    return (
      <div className="container-news py-12">
        <div className="text-center">
          <h2 className="font-playfair text-2xl font-bold text-primary-900 mb-4">{t('home.errorTitle')}</h2>
          <p className="text-primary-600 mb-6">{t('home.errorDesc')}</p>
          <button onClick={handleRefresh} className="btn-primary">{t('home.tryAgain')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-primary-50 border-b border-primary-200">
        <div className="container-news py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="headline-main mb-6">{t('home.title')}</h1>
            <p className="text-xl text-primary-700 leading-relaxed mb-8">{t('home.subtitle')}</p>
            <div className="flex items-center justify-center space-x-8 text-primary-600">
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">{articles.length}</div>
                <div className="text-sm">{t('home.storiesCount')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">97%</div>
                <div className="text-sm">{t('home.biasAccuracy')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">Global</div>
                <div className="text-sm">{t('home.coverage')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className="container-news py-6 border-b border-primary-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <button onClick={handleRefresh} disabled={isLoading} className="flex items-center space-x-2 text-primary-600 hover:text-primary-900 transition-colors disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>{isLoading ? t('home.loading') : t('home.refresh')}</span>
            </button>
            
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-primary-500" />
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="text-sm border-none bg-transparent text-primary-700 focus:outline-none">
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? t('cat.all') : t(`cat.${category}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-primary-600">{t('home.sortBy')}</span>
            <button onClick={() => setSortBy('date')} className={`text-sm px-3 py-1 transition-colors ${sortBy === 'date' ? 'bg-primary-900 text-white' : 'text-primary-600 hover:text-primary-900'}`}>
              {t('home.sortLatest')}
            </button>
            <button onClick={() => setSortBy('impact')} className={`text-sm px-3 py-1 transition-colors flex items-center ${sortBy === 'impact' ? 'bg-primary-900 text-white' : 'text-primary-600 hover:text-primary-900'}`}>
              <TrendingUp className="h-3 w-3 mr-1" />
              {t('home.sortImpact')}
            </button>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="container-news py-8">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="news-card">
                <div className="loading-skeleton h-48 mb-4"></div>
                <div className="p-6">
                  <div className="loading-skeleton h-4 mb-2"></div>
                  <div className="loading-skeleton h-4 w-3/4 mb-4"></div>
                  <div className="loading-skeleton h-3 mb-2"></div>
                  <div className="loading-skeleton h-3 w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredAndSortedArticles.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="font-playfair text-xl font-bold text-primary-900 mb-4">{t('home.noArticles')}</h3>
            <p className="text-primary-600">{t('home.noArticlesDesc')}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {featuredArticle && (
              <section>
                <h2 className="headline-section mb-6">{t('home.featured')}</h2>
                <div className="grid grid-cols-1">
                  <NewsCard article={featuredArticle} featured={true} />
                </div>
              </section>
            )}

            {regularArticles.length > 0 && (
              <section>
                <h2 className="headline-section mb-6">{t('home.latestImpact')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {regularArticles.map((article) => (
                    <NewsCard key={article.id} article={article} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="bg-primary-900 text-white">
        <div className="container-news py-16 text-center">
          <h2 className="font-playfair text-3xl font-bold mb-4">{t('cta.title')}</h2>
          <p className="text-xl text-primary-300 mb-8 max-w-2xl mx-auto">{t('cta.desc')}</p>
          <button className="btn-accent text-lg px-8 py-4">{t('cta.button')}</button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;